import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DataTable, SdiEdit, SdiShipMoving, SdiTrash, styles, TextLink } from '@sdir/sds';
import { useIntl } from 'react-intl';
import moment from 'moment';
import {
  OccupationReferenceExtended,
  Vessel as SeagoingVessel,
} from '@sdir/httpclient/lib/clients/aps/seagoing';
import { useNumberService } from '@sdir/utilities/lib/hooks/';
import { Country } from '@sdir/httpclient/lib/clients/core/search';
import { AddedSeagoing, AddedSeagoingDetails } from '../../../../../types';
import {
  SeagoingDetailsHeader,
  SeagoingDetailsHeaderMobile,
} from '../../../../../helpers/TableHeaders';
import { EditVesselModal } from './EditVesselModal';
import { EditDetailsModal } from './EditDetailsModal';
import { Card } from '../../../../Atoms';
import { SeagoingDetailsInput } from '../SeagoingDetailsSubForm';

interface SeagoingSummaryProps {
  occupations: OccupationReferenceExtended[];
  seagoing: AddedSeagoing;
  countries: Country[];
  onUpdate: (updatedSeagoing: AddedSeagoing) => void;
  onRemove: () => void;
}

const DateDisplayFormat = 'DD.MM.YYYY';

export const SeagoingSummary: React.FC<SeagoingSummaryProps> = ({
  occupations,
  seagoing,
  countries,
  onUpdate,
  onRemove,
}) => {
  const intl = useIntl();
  const numberService = useNumberService();

  const [openEditVessel, setOpenEditVessel] = useState(false);
  const [openEditDetails, setOpenEditDetails] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<any>();
  const [isMobile, setIsMobile] = useState(false);

  const handleEditDetails = useCallback((edited: SeagoingDetailsInput | AddedSeagoingDetails) => {
    setSelectedDetails(edited);
    setOpenEditDetails(true);
  }, []);

  const handleCancelEditDetails = useCallback(() => {
    setOpenEditDetails(false);
    setSelectedDetails(undefined);
  }, []);

  const handleRemoveDetails = useCallback(
    (removed: AddedSeagoingDetails) => {
      const newDetails = seagoing.details.filter((d) => d !== removed);
      if (newDetails.length) {
        onUpdate({ ...seagoing, details: newDetails });
      } else {
        onRemove();
      }
    },
    [onRemove, onUpdate, seagoing]
  );

  const handleUpdateDetails = useCallback(
    (edited?: SeagoingDetailsInput) => {
      const newSeagoingDetails = [...seagoing.details].map((d) => {
        if (d === selectedDetails) {
          if (!edited || !edited?.endDate || !edited.startDate) return d;
          const periodDiff = edited?.endDate?.diff(edited?.startDate);
          const totalDays = moment.duration(periodDiff).asDays() + 1;
          const hours = edited?.useHoursPerDay
            ? numberService.parseNumber(edited?.hoursPerDay || '') * totalDays
            : numberService.parseNumber(edited?.totalHours || '');
          return {
            totalHours: hours,
            existingReport: d.existingReport,
            occupationCode: edited?.occupationCode || '',
            startDate: edited.startDate,
            endDate: edited.endDate,
          };
        }
        return d;
      });
      onUpdate({ ...seagoing, details: newSeagoingDetails });
      handleCancelEditDetails();
    },
    [onUpdate, handleCancelEditDetails, numberService, seagoing, selectedDetails]
  );

  const handleUpdateVessel = useCallback(
    (newVessel: SeagoingVessel) => onUpdate({ ...seagoing, vessel: newVessel }),
    [onUpdate, seagoing]
  );

  const handleEditVessel = useCallback(() => setOpenEditVessel(true), []);
  const handleCancelEditVessel = useCallback(() => setOpenEditVessel(false), []);

  const detailsData = useMemo(
    () =>
      seagoing.details?.map((d) => {
        const periodDiff = d?.endDate?.diff(d?.startDate);
        const totalDays = moment.duration(periodDiff).asDays() + 1;
        const hoursPerDay = (d?.totalHours || 0) / totalDays;
        return {
          jobTitle: occupations.find((o) => o.code === d.occupationCode)?.title || '',
          periodStart: d.startDate.format(DateDisplayFormat),
          periodEnd: d.endDate.format(DateDisplayFormat),
          hours: numberService.formatDecimal(hoursPerDay),
          actions: (
            <ActionsWrapper>
              <TextLink
                data-testid="seagoing-edit-button"
                icon={<SdiEdit size="s" />}
                text=""
                click={() => handleEditDetails(d)}
              />
              <TextLink
                data-testid="seagoing-delete-button"
                icon={<SdiTrash size="s" />}
                text=""
                click={() => handleRemoveDetails(d)}
              />
            </ActionsWrapper>
          ),
          childContent: isMobile ? (
            <RowMobileContent
              rowData={d}
              fm={intl.formatMessage}
              ns={numberService}
              handleRemoveDetails={handleRemoveDetails}
            />
          ) : null,
        };
      }),
    [
      handleRemoveDetails,
      handleEditDetails,
      numberService,
      occupations,
      seagoing.details,
      intl.formatMessage,
      isMobile,
    ]
  );

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Container>
      <div className="top-content">
        <div>
          {seagoing.vessel && (
            <InfoBlock
              icon={<SdiShipMoving size="s" />}
              title={seagoing.vessel.name || ''}
              details={seagoing.vessel.callSign || ''}
            />
          )}
        </div>
        <div>
          <TextLink
            text={intl.formatMessage({ id: 'addseagoing.button.edit' })}
            click={handleEditVessel}
          />
        </div>
      </div>
      {detailsData &&
        (isMobile ? (
          <StyledDataTable
            columns={SeagoingDetailsHeaderMobile(intl.formatMessage)}
            data={detailsData}
            collapsable
            collapsableText={[
              intl.formatMessage({ id: 'general.table.viewdetails' }),
              intl.formatMessage({ id: 'general.table.hidedetails' }),
            ]}
          />
        ) : (
          <StyledDataTable columns={SeagoingDetailsHeader(intl.formatMessage)} data={detailsData} />
        ))}
      <EditVesselModal
        isOpen={openEditVessel}
        countries={countries}
        seagoing={seagoing}
        onClose={handleCancelEditVessel}
        onConfirm={handleUpdateVessel}
      />
      <EditDetailsModal
        isOpen={openEditDetails}
        occupations={occupations}
        seagoingDetails={selectedDetails}
        onClose={handleCancelEditDetails}
        onConfirm={handleUpdateDetails}
      />
    </Container>
  );
};

const BaseInfoBlock: React.FC<{
  icon: ReactNode;
  title: string;
  details: string;
  className?: string;
}> = ({ icon, title, details, className }) => (
  <div className={className}>
    <div className="info-icon">{icon}</div>
    <div className="info-title">{title}</div>
    <div className="info-details">{details}</div>
  </div>
);

interface RowMobileContentProps {
  rowData: AddedSeagoingDetails;
  fm: any;
  ns: any;
  handleRemoveDetails: (v: AddedSeagoingDetails) => void;
}

const RowMobileContent: React.FC<RowMobileContentProps> = ({
  rowData,
  fm,
  ns,
  handleRemoveDetails,
}) => {
  return (
    <>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'addseagoing.table.periodStart' })}</SubHeader>
        <SubValue>{rowData.startDate.format(DateDisplayFormat)}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'addseagoing.table.periodEnd' })}</SubHeader>
        <SubValue>{rowData.endDate.format(DateDisplayFormat)}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'addseagoing.table.hours' })}</SubHeader>
        <SubValue>{ns.formatDecimal(rowData.totalHours)}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <TextLink
          data-testid="seagoing-delete-button"
          icon={<SdiTrash size="s" />}
          text=""
          click={() => handleRemoveDetails(rowData)}
        />
      </MobileContentItem>
    </>
  );
};

const InfoBlock = styled(BaseInfoBlock)`
  display: grid;
  margin-bottom: 2rem;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  grid-column-gap: 1rem;

  .info-icon {
    grid-column: 1;
    grid-row: 1 / span 2;
  }

  .info-title {
    grid-column: 2;
    grid-row: 1;
    line-height: 1em;
    font-family: ${styles.fonts.stfMedium};
  }

  .info-details {
    grid-column: 2;
    grid-row: 2;
    color: ${styles.colors.font.text50};
  }
`;

const Container = styled(Card)`
  margin-bottom: 5rem;
  width: auto;

  .top-content {
    display: flex;

    > *:first-child {
      flex: 1 1;
    }
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  & > button:first-child {
    margin-right: 5px;
  }
`;

const StyledDataTable = styled(DataTable)`
  &&& {
    padding: 0;

    th:first-child,
    td:first-child {
      padding-left: 0;
    }

    th:last-child,
    td:last-child {
      padding-right: 0;
    }

    th {
      color: #333333a6;
      font-family: ${styles.fonts.stfMedium};
      font-size: 14px;
      white-space: nowrap;
    }
  }
`;

const MobileContentItem = styled.div`
  margin-bottom: 10px;
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;

export default SeagoingSummary;
